import dayjs from 'dayjs';
import _ from 'lodash';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { RhAuthorInfo } from './RhAuthorInfo';
import { RhCard } from './RhCard';
import { RhImage, RhImageMode } from './RhImage';
import { SizedBox } from './SizedBox';
import { Description, H3, H4 } from './Styles';

type Props = {
  /** the leading image. it won't display anything if no value was provided */
  imageUrl?: string | null;
  imageMode?: RhImageMode;
  title?: string | null;
  /** The main text grouped in the [RhAuthorInfo] component */
  author?: string;
  /** The text that overrides the default author label */
  authorLabelOverride?: string;
  /** The text grouped in the [RhAuthorInfo] component */
  dateUpdated?: string;
  description?: React.ReactNode;
  /** Whether or not to cut off long description with an ellipsis or not. DEFAULT: true */
  isDescriptionTruncated?: boolean;
  /** The small and greyed-out text shown on the very top of the card */
  startTime?: string;
  /** The avatar image grouped in the [RhAuthorInfo] component */
  authorImage?: string;
  /** the component on the rightmost of the card */
  trailing?: React.ComponentType;
  /** the components on the top-rightmost of the card  */
  topRightActions?: React.ReactNode;
  isHoverable?: boolean;
  isThumbnailDisabled?: boolean;
  divStyle?: React.CSSProperties;
  onClick?: () => void;
};

export const RhCardListItem = forwardRef<HTMLDivElement, Props>((props, ref) => {
  return (
    <RhCard
      ref={ref}
      // "tiled" adds bigger padding. Set mode as "tiled" if there is no image
      mode={props.imageUrl ? 'mini' : 'tiled'}
      hoverable={props.isHoverable}
      style={props.divStyle}>
      <RelativeDiv>
        <ProgramContent hasTrailing={!!props.trailing} onClick={props.onClick}>
          {!props.isThumbnailDisabled && <RhImage mode={props.imageMode} src={props.imageUrl || ''} />}
          <div>
            {!!props.startTime && (
              <>
                <H4 up>{dayjs(props.startTime).format('ddd, MMM DD, YYYY | hh:mmA')}</H4>
                <SizedBox height={8} />
              </>
            )}
            {!!props.title && <H3>{props.title || '-'}</H3>}
            {!!props.author && (
              <>
                <SizedBox height={8} />
                <RhAuthorInfo
                  mode={props.dateUpdated ? 'normal' : 'mini'}
                  authorLabel={props.authorLabelOverride}
                  author={props.author}
                  subtitleDate={props.dateUpdated || new Date().toJSON()}
                  authorImage={props.authorImage}
                />
              </>
            )}
            {!!props.description &&
              (typeof props.description === 'string' ? (
                <>
                  <SizedBox height={8} />
                  <Description>
                    {props.isDescriptionTruncated
                      ? _.truncate(props.description, { length: 150, separator: ' ' })
                      : props.description}
                  </Description>
                </>
              ) : (
                props.description
              ))}
          </div>
          {!!props.trailing ? <props.trailing /> : null}
        </ProgramContent>
        {!!props.topRightActions && <TopRightActions>{props.topRightActions}</TopRightActions>}
      </RelativeDiv>
    </RhCard>
  );
});

RhCardListItem.defaultProps = {
  isDescriptionTruncated: true,
};

const ProgramContent = styled.div<{ hasTrailing: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasTrailing = false }) => (hasTrailing ? `auto 3fr 1fr` : `auto 1fr`)};
  grid-gap: 15px;
`;

const RelativeDiv = styled.div`
  position: relative;
`;

const TopRightActions = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  & > :not(:last-child) {
    margin-right: 11px;
  }
`;
